<template>
    <div class="ah-page">
        <div class="ah-header">
            <div class="ah-header-left">
                <div class="ah-icon-wrap"><i class="fas fa-calendar-alt"></i></div>
                <h2 class="ah-title">Holidays</h2>
                <span class="ah-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.CREATE_HOLIDAYS">
                <button class="ah-add-btn" @click="$router.push('/attendance-holidays/create')">
                    <i class="fas fa-plus"></i> Add Holiday
                </button>
            </AllowPermission>
        </div>

        <div v-if="loading" class="ah-loading"><div class="ah-spinner"></div><span>Loading holidays...</span></div>
        <div v-else-if="!items.length" class="ah-empty">
            <div class="ah-empty-icon"><i class="fas fa-calendar-alt"></i></div>
            <h3>No holidays found</h3>
        </div>
        <div v-else class="ah-grid">
            <div class="ah-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="ah-card-top">
                    <div class="ah-card-icon" :class="ahColor(idx)"><i class="fas fa-gift"></i></div>
                    <div class="ah-card-info">
                        <h4 class="ah-card-name">{{ item.name }}</h4>
                        <span class="ah-card-type" v-if="item.holidayType">{{ item.holidayType.name }}</span>
                    </div>
                </div>
                <div class="ah-card-details">
                    <div class="ah-detail">
                        <span class="ah-detail-label">Start Date</span>
                        <span class="ah-detail-val">{{ item.startDateFormatted || '—' }}</span>
                    </div>
                    <div class="ah-detail">
                        <span class="ah-detail-label">End Date</span>
                        <span class="ah-detail-val">{{ item.endDateFormatted || '—' }}</span>
                    </div>
                    <div class="ah-detail" v-if="item.country">
                        <span class="ah-detail-label">Country</span>
                        <span class="ah-detail-val">{{ item.country.name }}</span>
                    </div>
                </div>
                <div class="ah-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_HOLIDAYS">
                        <button class="ah-action-btn ah-btn-edit" @click="$router.push(`/attendance-holidays/${item.id}`)"><i class="fas fa-pen"></i> Edit</button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_HOLIDAYS">
                        <button class="ah-action-btn ah-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../../store/permission"
import AllowPermission from "../../../components/layout/AllowPermission.vue"

export default {
    components: { AllowPermission },
    data() {
        return {
            loading: true,
            items: [],
            PERMISSION_CODES,
        }
    },
    mounted() { this.fetchData() },
    methods: {
        ahColor(i) { return ["ah-clr-blue","ah-clr-purple","ah-clr-teal","ah-clr-amber","ah-clr-rose"][i % 5] },
        async fetchData() {
            this.loading = true
            try { const res = await http.get("/attendance-holidays"); this.items = res.data.result || [] } catch (err) { console.log(err) }
            this.loading = false
        },
        async deleteItem(id) {
            if (!confirm("Delete this holiday?")) return
            try { await http.delete(`/attendance-holidays/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch (e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";
.ah-page { animation: ahFade .3s ease; }
@keyframes ahFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.ah-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.ah-header-left { display:flex; align-items:center; gap:10px; }
.ah-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.ah-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.ah-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.ah-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }
.ah-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.ah-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:ahSpin .7s linear infinite; }
@keyframes ahSpin { to { transform:rotate(360deg); } }
.ah-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.ah-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.ah-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.ah-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:ahSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes ahSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.ah-card-top { display:flex; align-items:flex-start; gap:10px; }
.ah-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.ah-clr-blue { background:#eff6ff; color:#2563eb; }
.ah-clr-purple { background:#f5f3ff; color:#7c3aed; }
.ah-clr-teal { background:#f0fdfa; color:#0d9488; }
.ah-clr-amber { background:#fffbeb; color:#d97706; }
.ah-clr-rose { background:#fff1f2; color:#e11d48; }
.ah-card-info { flex:1; min-width:0; }
.ah-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0 0 2px; line-height:1.3; }
.ah-card-type { font-size:10.5px; font-weight:600; padding:2px 8px; border-radius:$radiusFull; background:#f5f3ff; color:#7c3aed; display:inline-block; }
.ah-card-details { display:flex; flex-wrap:wrap; gap:6px; }
.ah-detail { flex:1; min-width:80px; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:6px 10px; display:flex; flex-direction:column; gap:2px; }
.ah-detail-label { font-size:10px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; }
.ah-detail-val { font-size:12px; color:$gray700; font-weight:600; }
.ah-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.ah-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; i { font-size:10px; } }
.ah-btn-edit { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.ah-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }
@media(max-width:992px) { .ah-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .ah-grid { grid-template-columns:1fr; } }
</style>
